<template>
  <Dialog
    v-if="isDialog"
    :value="formDialogs"
    :persistent="isPersistent"
    :progress-bar-value="(100 / possibleSteps.length) * currentStep"
    :show-progress-bar="showProgressBar"
    :loading="loading"
    :show-back-arrow="showBackArrow"
    :buttons="dialogButtons"
    :header-type="dialogHeaderType"
    :left-header-icon="dialogLeftHeaderIcon"
    :left-header-icon-type="dialogLeftHeaderIconType"
    :left-header-icon-component="dialogLeftHeaderIconComponent"
    :title="dialogTitle"
    :width="dialogWidth"
    :fullscreen="dialogWidth ? dialogWidth > windowSize.x : undefined"
    :text-class="dialogTextClass"
    :dialog-content-justify="dialogContentJustify"
    :dialog-content-display-classes="dialogContentDisplayClasses"
    @input="closeDialog"
    @clickBack="clickBack"
  >
    <template #text>
      <template v-if="!hideContent">
        <div v-if="!formSend" :id="'form_holder_' + formName">
          <DynamicForm
            :form-name="formName"
            :error-scroll-container="errorScrollContainer"
            :is-sending="isSending"
            :is-dialog="isDialog"
            @close="closeDialog"
            @next-click="nextClickAction"
            @prev-click="prevClickAction"
          />
        </div>
        <div v-else class="after-steps">
          <div v-for="(afterFormStep, index) in stepsAfterForm" :key="index">
            <component
              :is="afterFormStep.cmp"
              v-if="index === afterFormStepIndex"
              :form-name="formName"
              v-bind="afterFormStep.params"
              :identifier="identifier"
              @close="closeDialog"
            />
          </div>
        </div>
      </template>
    </template>
  </Dialog>
  <div v-else>
    <div class="form_holder">
      <v-progress-linear
        v-if="!loading && !formSend && !isMobileApp"
        :value="(100 / possibleSteps.length) * currentStep"
        height="28"
        class="progress_bar"
      />
      <v-card v-if="!formSend" class="px-0" :elevation="0" :class="{ 'pt-10 pb-5': !isMobileApp }">
        <Loading v-if="loading" />
        <template v-else>
          <v-card-title
            v-if="possibleSteps[currentStep - 1]?.settings?.title || $vuetify.breakpoint.smAndDown"
            class="px-4 px-md-10"
          >
            <DialogHeader
              :show-back-arrow="currentStep > 1"
              :show-close="($vuetify.breakpoint.smAndDown && isDialog) || isMobileApp"
              @close="closeDialog"
              @clickBack="
                updateCurrentStep({
                  formName: formName,
                  step: currentStep - 1
                })
              "
            >
              {{ getLocaleText(possibleSteps[currentStep - 1]?.settings?.title) }}
            </DialogHeader>
          </v-card-title>
          <v-card-text
            :id="errorScrollContainer.slice(1)"
            class="my-0 pa-0"
            :class="{ 'pb-4': !showNavigationButton }"
          >
            <div class="px-4 px-md-10">
              <template v-if="!formSend">
                <DynamicForm
                  :form-name="formName"
                  :gtm-form-name="gtmFormName"
                  :error-scroll-container="errorScrollContainer"
                  scrolling-container="body"
                  :use-old-gtm="useOldGtm"
                  :is-sending="isSending"
                  :is-dialog="isDialog"
                  @next-click="nextClickAction"
                  @prev-click="prevClickAction"
                />
              </template>
            </div>
          </v-card-text>
        </template>
      </v-card>
      <template v-else-if="formSend">
        <div v-for="(afterFormStep, index) in stepsAfterForm" :key="index">
          <component
            :is="afterFormStep.cmp"
            v-if="index === afterFormStepIndex"
            :form-name="formName"
            v-bind="afterFormStep.params"
            :identifier="identifier"
            @close="closeDialog"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import dynamicForms from '@/mixins/dynamicForms';
import platformId from '@/mixins/platformId';
import checkUserVerification from '@/mixins/checkUserVerification';
import phoneVerification from '@/mixins/phoneVerification';
import preSignedUpload from '@/mixins/preSignedUpload';
import dialogGtm from '@/mixins/dialogGtm';
import GoPay from '@/components/Molecules/GoPay';
import BloodTestFinalStep from '@/components/Molecules/BloodTestFinalStep';
import ThematicCounsellingThankYou from '@/components/Molecules/ThematicCounsellingThankYou';
import { patchUser } from '@/services/user/userService.js';

export default {
  name: 'DynamicFormHolder',
  components: {
    BloodTestFinalStep,
    GoPay,
    ThematicCounsellingThankYou
  },
  mixins: [
    dynamicForms,
    platformId,
    checkUserVerification,
    phoneVerification,
    preSignedUpload,
    dialogGtm
  ],
  props: {
    isDialog: {
      type: Boolean,
      default: false
    },
    formName: {
      type: String,
      required: true
    },
    formSentAction: {
      type: Function,
      required: false
    },
    loadDefaultData: {
      type: Boolean,
      default: true
    },
    stepsBefore: {
      type: Array,
      required: false
    },
    stepsAfterForm: {
      type: Array,
      required: false
    },
    restoreRoute: {
      type: Boolean,
      default: true
    },
    updateRouteOnCreate: {
      type: Boolean,
      default: false
    },
    useOldGtm: {
      type: Boolean,
      default: false
    },
    gtmFormName: {
      type: String,
      required: false
    },
    useNeedHelp: {
      type: Boolean,
      default: true
    },
    ignoreAuthUser: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSending: false,
      formSend: false,
      afterFormStepIndex: 0,
      identifier: '',
      hideContent: false
    };
  },
  computed: {
    formType() {
      return this.$store.state.dynamicForms.forms[this.formName]?.formType;
    },
    formNameGtm() {
      let formNameGtm = '';
      switch (this.formName) {
        case 'Prevention':
          formNameGtm = 'kz-prevence';
          break;
        case 'Order':
          formNameGtm = 'kz-objednani';
          break;
        case 'Question':
          formNameGtm = 'kz-dotaz';
          break;
        case 'onco-prevention':
          formNameGtm = 'kz-onco-prevention';
          break;
        case 'videocall':
          formNameGtm = 'videocall';
          break;
        case 'videotherapy':
          formNameGtm = 'videotherapy';
          break;
        case 'interpretation-of-results':
          formNameGtm = 'kz-interpretace';
          break;
        default:
          formNameGtm = this.gtmFormName ? this.gtmFormName : this.formName;
      }
      return formNameGtm;
    },
    errorScrollContainer() {
      if (this.isDialog && this.formName) {
        return '#form_holder_' + this.formName;
      }
      return '#dynamic_form';
    },
    formDialogs: {
      get() {
        return this.$store.state.dynamicForms.formDialogs[this.formName] || false;
      },
      set(value) {
        this.$store.commit('dynamicForms/updateFormDialogs', { formName: this.formName, value });
      }
    },
    loading: {
      get() {
        if (
          this.currentStepData?.dialogDataStore &&
          this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogLoading`]
        ) {
          return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogLoading`];
        }
        return this.$store.getters['dynamicForms/loading'][this.formName] === undefined
          ? true
          : this.$store.getters['dynamicForms/loading'][this.formName];
      }
    },
    isPersistent() {
      if (!this.formSend) {
        if (this.currentStepData?.settings?.stepSettings?.isPersistent !== undefined) {
          return this.currentStepData.settings.stepSettings.isPersistent;
        }
      } else {
        if (
          this.stepsAfterForm[this.afterFormStepIndex]?.stepSettings?.isPersistent !== undefined
        ) {
          return this.stepsAfterForm[this.afterFormStepIndex].stepSettings.isPersistent;
        }
      }
      return false;
    },
    ...mapGetters({
      headerType: 'dynamicForms/headerType',
      verificationResult: 'profile/phoneVerification/verificationResult',
      windowSize: 'general/windowSize'
    }),
    dialogTitle() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogTitle`] !== undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogTitle`];
      }

      if (
        this.formSend &&
        this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore &&
        this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex].dialogDataStore}/dialogTitle`
        ] !== undefined
      ) {
        return this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex].dialogDataStore}/dialogTitle`
        ];
      }

      return this.$store.getters['dynamicForms/dialogTitle'];
    },
    dialogTextClass() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogTextClass`] !== undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogTextClass`];
      }

      if (this.formSend && this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore) {
        return this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore}/dialogTextClass`
        ];
      }

      return undefined;
    },
    dialogButtons() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogButtons`] !== undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogButtons`];
      }

      if (this.formSend && this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore) {
        return this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore}/dialogButtons`
        ];
      }

      return this.$store.getters['dynamicForms/dialogButtons'];
    },
    dialogLeftHeaderIconType() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogLeftHeaderIconType`] !==
          undefined
      ) {
        return this.$store.getters[
          `${this.currentStepData.dialogDataStore}/dialogLeftHeaderIconType`
        ];
      }

      if (this.formSend && this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore) {
        return this.$store.getters[
          `${
            this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore
          }/dialogLeftHeaderIconType`
        ];
      }

      return this.$store.getters['dynamicForms/dialogLeftHeaderIconType'];
    },
    dialogLeftHeaderIconComponent() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[
          `${this.currentStepData.dialogDataStore}/dialogLeftHeaderIconComponent`
        ] !== undefined
      ) {
        return this.$store.getters[
          `${this.currentStepData.dialogDataStore}/dialogLeftHeaderIconComponent`
        ];
      }

      if (this.formSend && this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore) {
        return this.$store.getters[
          `${
            this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore
          }/dialogLeftHeaderIconComponent`
        ];
      }

      return this.$store.getters['dynamicForms/dialogLeftHeaderIconComponent'];
    },
    showProgressBar() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/showProgressBar`] !== undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/showProgressBar`];
      }

      return this.isDialog && !this.formSend;
    },
    showBackArrow() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/showBackArrow`] !== undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/showBackArrow`];
      }

      return this.currentStep > 1 && this.dialogHeaderType !== 'information';
    },
    dialogLeftHeaderIcon() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogLeftHeaderIcon`] !==
          undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogLeftHeaderIcon`];
      }

      if (this.formSend && this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore) {
        return this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore}/dialogLeftHeaderIcon`
        ];
      }

      return this.$store.getters['dynamicForms/dialogLeftHeaderIcon'];
    },
    dialogHeaderType() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogHeaderType`] !==
          undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogHeaderType`];
      }

      if (
        this.formSend &&
        this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore &&
        this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore}/dialogHeaderType`
        ] !== undefined
      ) {
        return this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore}/dialogHeaderType`
        ];
      }

      return this.headerType;
    },
    dialogWidth() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogWidth`] !== undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogWidth`];
      }

      if (
        this.formSend &&
        this.stepsAfterForm[this.afterFormStepIndex]?.dialogDataStore &&
        this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex].dialogDataStore}/dialogWidth`
        ] !== undefined
      ) {
        return this.$store.getters[
          `${this.stepsAfterForm[this.afterFormStepIndex].dialogDataStore}/dialogWidth`
        ];
      }

      return undefined;
    },
    dialogContentJustify() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogContentJustify`] !==
          undefined
      ) {
        return this.$store.getters[`${this.currentStepData.dialogDataStore}/dialogContentJustify`];
      }

      return undefined;
    },
    dialogContentDisplayClasses() {
      if (
        this.currentStepData?.dialogDataStore &&
        this.$store.getters[
          `${this.currentStepData.dialogDataStore}/dialogContentDisplayClasses`
        ] !== undefined
      ) {
        return this.$store.getters[
          `${this.currentStepData.dialogDataStore}/dialogContentDisplayClasses`
        ];
      }

      return undefined;
    }
  },
  watch: {
    currentStep(newValue, oldValue) {
      if (this.formDialogs && !!newValue && Math.abs(newValue - (!oldValue ? 0 : oldValue)) === 1) {
        if (this.formNameGtm) {
          this.gtmPush({ event: `form_progress_${newValue}`, 'form.name': this.formNameGtm });
        }
      }
      if (this.formDialogs) {
        //update route only if dialog is open / form is shown
        this.updateRoute();
      }
      if (this.isDialog) {
        this.dialogScrollTop();
      }
    },
    async formDialogs(newValue, oldValue) {
      if (newValue) {
        this.hideContent = false;
        this.formSend = false;
        if (!this.updateRouteOnCreate) {
          await this.updateRoute();
        }
        if (this.$auth.user && !this.ignoreAuthUser) {
          this.dialogChanged(this.formNameGtm, true);
          if (!this.currentStep || this.currentStep === 1) {
            this.gtmPush({
              event: 'form_start',
              'form.name': this.formNameGtm,
              siteSection: 'klientska_zona'
            });
            this.gtmPush({
              event: 'form_started',
              'form.name': this.formNameGtm,
              siteSection: 'klientska_zona'
            });
            this.gtmPush({
              event: `form_progress_1`,
              'form.name': this.formNameGtm,
              siteSection: 'klientska_zona'
            });
          }

          if (this.useNeedHelp) {
            this.$store.commit('general/setShowNeedHelp', true);
            this.$store.commit('general/setNeedHelpInverted', true);
          }
        }
      } else if (oldValue !== undefined) {
        //don't update route on formDialogs setup (from setup form from store)
        if (this.$auth.user) {
          await this.checkUserVerification();
        }
        this.afterFormStepIndex = 0;
        this.updateRoute(null, null);
        if (this.$auth.user) {
          this.$store.commit('general/setShowNeedHelp', false);
          this.dialogChanged(this.formNameGtm, false);
          this.gtmPush({
            event: 'form_close',
            'form.name': this.formNameGtm,
            'form.step': this.currentStep ?? 1,
            siteSection: 'klientska_zona'
          });
        }
        setTimeout(() => {
          this.hideContent = true;
        }, 500);
      }
    },
    $route(to) {
      this.checkRoute(to.query, this.isDialog);
    }
  },
  async mounted() {
    await this.getFormData({
      formName: this.formName,
      loadDefaultData: this.loadDefaultData,
      region: this.$domainRegion,
      stepsBefore: this.stepsBefore,
      updateRouteOnCreate: this.updateRouteOnCreate
    }).then(() => {
      this.$emit('formLoaded');
    });

    if (this.isDialog && this.useNeedHelp && this.$route.query.dfn === this.formName) {
      this.$store.commit('general/setShowNeedHelp', true);
      this.$store.commit('general/setNeedHelpInverted', true);
    }
  },
  created() {
    if (this.updateRouteOnCreate) {
      this.updateRoute();
    }
  },
  destroyed() {
    if (
      this.restoreRoute &&
      this.$auth.user &&
      !!this.$route.query.dfn &&
      this.$route.query.dfn === this.formName
    ) {
      //update route only if user is logged in, otherwise it breaks logout proces
      this.updateRoute(null, null);
    }
  },
  methods: {
    ...mapActions({
      getFormData: 'dynamicForms/getFormData',
      updateCurrentStep: 'dynamicForms/updateCurrentStep',
      getAllDefaultFormData: 'dynamicForms/getAllDefaultFormData',
      setReturnData: 'dynamicForms/setReturnData'
    }),

    clickBack() {
      if (
        this.currentStepData?.dialogDataStore &&
        typeof this.$store.getters[`${this.currentStepData.dialogDataStore}/clickBack`] ===
          'function'
      ) {
        this.$store.getters[`${this.currentStepData.dialogDataStore}/clickBack`]();
        return;
      }

      this.updateCurrentStep({
        formName: this.formName,
        step: this.currentStep - 1
      });
    },
    prevClickAction() {
      if (this.currentStepData?.settings?.buttonsSettings?.prevButtonClickAction !== undefined) {
        this.currentStepData.settings.buttonsSettings.prevButtonClickAction();
        return;
      }
      this.updateCurrentStep({
        formName: this.formName,
        step: this.currentStep - 1
      });
      this.$nextTick(() => {
        if (!this.isDialog) {
          this.$scrollTo(this.errorScrollContainer, { duration: 300, force: true, offset: -150 });
        } else {
          this.$scrollTo(this.errorScrollContainer, {
            container: this.errorScrollContainer,
            duration: 300,
            force: true
          });
        }
      });
    },

    async nextClickAction() {
      try {
        //check if form has phone input and if currentStep is after step with phone input
        let stepWithPhoneInput = -1;
        for (let index = 0; index < this.internalForms[this.formName].length; index++) {
          if (this.currentStep < index) {
            break;
          }
          if (this.internalForms[this.formName][index].phoneVerificationInput) {
            stepWithPhoneInput = index;
            this.isSending = true;
            await this.$auth.fetchUser();
            this.isSending = false;
          }
        }

        //if form has phone input, and currentStep is after phoneInput step and phone is not verified, return to step with phone input
        if (
          stepWithPhoneInput > -1 &&
          stepWithPhoneInput < this.currentStep - 1 &&
          !this.$auth.user.verify.phone
        ) {
          await this.updateCurrentStep({
            formName: this.formName,
            step: stepWithPhoneInput + 1
          });
          return;
        }

        if (
          this.currentStep === this.possibleSteps.length ||
          (this.forms[this.formName].saveEachStep && this.currentStepData.type === 'dynamic')
        ) {
          await this.sendForm(this.currentStep === this.possibleSteps.length);
          if (this.currentStep === this.possibleSteps.length) {
            return;
          }
        }
        if (this.currentStepData.phoneVerificationInput) {
          if (this.formType !== 'static') {
            let shownPhoneInput = null;
            this.currentStepData.phoneVerificationInput.forEach(input => {
              if (this.showInput(input, this.formName)) {
                shownPhoneInput = input.id;
              }
            });
            if (shownPhoneInput) {
              if (this.responses[this.formName][shownPhoneInput] !== this.$auth.user.phone) {
                this.isSending = true;
                try {
                  await patchUser(this.$http, this.$auth, {
                    phone: this.phoneFormatted(this.responses[this.formName][shownPhoneInput])
                  });
                } catch (err) {
                  this.handleError({ error: err, code: '0x00000070' });
                } finally {
                  this.isSending = false;
                }
              }
              if (!this.$auth.user.verify.phone) {
                await this.resendPhoneVerification();
              }
            }
          } else {
            if (!this.$auth.user.verify.phone) {
              await this.resendPhoneVerification();
            }
          }
        }
        this.updateCurrentStep({
          formName: this.formName,
          step: this.currentStep + 1
        });
      } catch (err) {
        this.handleError({ error: err, code: '0x0000000F' });
      }
      this.$nextTick(() => {
        if (!this.isDialog) {
          this.$scrollTo(this.errorScrollContainer, { duration: 300, force: true, offset: -150 });
        } else {
          this.$scrollTo(this.errorScrollContainer, {
            container: this.errorScrollContainer,
            duration: 300,
            force: true
          });
        }
      });
    },

    async sendForm(isLastStep = false) {
      let formData = [];
      this.isSending = true;

      if (this.$auth.isLoggedIn && !(await this.checkUserVerification(false, true))) {
        this.isSending = false;
        return false;
      }

      await Promise.allSettled(
        Object.keys(this.responses[this.formName]).map(async formInputId => {
          let input = this.findInputById(formInputId, this.formName);
          if (
            this.showInput(input, this.formName) &&
            this.responses[this.formName][formInputId] !== undefined
          ) {
            if (Array.isArray(this.responses[this.formName][formInputId])) {
              this.responses[this.formName][formInputId].forEach(value => {
                if (value !== null) {
                  if (value.uuid) {
                    formData.push({
                      formInputId: formInputId,
                      value: value.value,
                      fileName: value.name
                    });
                  } else {
                    if (input.inputValidation === 'integer') {
                      formData.push({
                        formInputId: formInputId,
                        value: +value
                      });
                    } else {
                      formData.push({
                        formInputId: formInputId,
                        value: value.toString()
                      });
                    }
                  }
                }
              });
            } else if (this.responses[this.formName][formInputId] !== null) {
              formData.push({
                formInputId: formInputId,
                value: this.responses[this.formName][formInputId].toString()
              });
            }
          }
        })
      );

      await Promise.allSettled(
        Object.keys(this.attachments[this.formName]).map(async formInputId => {
          let input = this.findInputById(formInputId, this.formName);
          if (
            this.showInput(input, this.formName) &&
            this.attachments[this.formName][formInputId].length
          ) {
            const uploadedFiles = await this.processFiles(
              this.attachments[this.formName][formInputId],
              error => {
                this.handlePreSignedError(error, formInputId, () => {
                  const inputStep = this.forms[this.formName].formSteps.findIndex(step =>
                    step.formInputs.some(input => input.id === formInputId)
                  );
                  this.updateCurrentStep({
                    formName: this.formName,
                    step: inputStep + 1
                  });
                });
              }
            );

            formData.push(
              ...uploadedFiles.map(uploadedFile => ({
                formInputId,
                value: `${uploadedFile.id}`
              }))
            );
          }
        })
      );

      let languageCode = this.$i18n.locale;

      if (this.$auth.isLoggedIn && this.isDialog) {
        languageCode = this.$auth.user.language;
      }

      const data = {
        languageCode: languageCode,
        platformId: this.isMobileApp ? this.platformId : 1,
        formItems: formData
      };

      if (this.isPutinSucks) {
        data.gwCallbackUrl = window.location.href + '&gpt=1';
      }

      this.$http
        .post(`/v3/fe/forms/${this.formName}/template/${this.forms[this.formName].id}`, data)
        .then(async responseData => {
          await this.setReturnData(responseData);
          if (this.isDialog) {
            await this.$auth.fetchUser();
          }

          if (isLastStep) {
            this.formSentAction();
            this.identifier = responseData.identifier;
            if (this.stepsAfterForm[this.afterFormStepIndex].callback !== undefined) {
              this.stepsAfterForm[this.afterFormStepIndex].callback(this.identifier);
            }
            this.formSend = true;

            if (this.loadDefaultData) {
              this.getAllDefaultFormData();
            }

            this.updateCurrentStep({
              formName: this.formName,
              step: 1
            });
          }

          // TODO: upravit aby to bylo generické
          if (this.useOldGtm) {
            this.gtmPush({
              event: 'form_sent_success',
              'form.name': this.gtmFormName ?? this.formName
            });
          }
        })
        .catch(err => {
          this.formDialogs = false;
          this.handleError({ error: err, code: '0x00000010' });
        })
        .finally(() => {
          this.isSending = false;
        });
    },
    closeDialog() {
      this.formDialogs = false;
      if (this.formType === 'static') {
        this.updateCurrentStep({
          formName: this.formName,
          step: 1
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.form_holder {
  position: relative;
  width: 100%;
}

.after-steps {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.progress_bar {
  position: absolute;
  top: -8px;
  left: 0;
  border-top-right-radius: map-get($rounded, 'xl');
  border-top-left-radius: map-get($rounded, 'xl');
}
</style>
